/**
 * Guessing a report's date range from its file name.
 *
 * The Business Report by child ASIN is a total over whatever range was
 * selected in Seller Central, and the file itself does not carry that range,
 * so the uploader has to state it. When the file name already spells the range
 * out — the way a Seller Central download or a hand-named export usually does —
 * this fills the Start and End inputs so the uploader only has to confirm.
 *
 * A guess is only offered when the name clearly holds a range or a whole
 * month. Anything ambiguous returns null and the inputs stay empty, because a
 * wrong prefill is worse than none: it would stamp every row with the wrong
 * period.
 *
 * Pure and side-effect free; unit-tested against invented file names.
 */

export interface FilenamePeriod {
  /** YYYY-MM-DD */
  start: string;
  /** YYYY-MM-DD */
  end: string;
}

const MONTH_LENGTHS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

/** The last calendar day of a month, leap years included. */
function lastDayOfMonth(year: number, month: number): number {
  if (month === 2 && year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0)) return 29;
  return MONTH_LENGTHS[month - 1] ?? 31;
}

/** Whether a Y/M/D triple is a real calendar day. */
function isRealDay(year: number, month: number, day: number): boolean {
  if (month < 1 || month > 12) return false;
  if (day < 1) return false;
  return day <= lastDayOfMonth(year, month);
}

/** A two- or four-digit year to a full year; `26` becomes `2026`. */
function fullYear(raw: string): number {
  const n = Number(raw);
  if (raw.length <= 2) return 2000 + n;
  return n;
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function ymd(year: number, month: number, day: number): string {
  return `${year}-${pad(month)}-${pad(day)}`;
}

/**
 * Reads a range or a whole month out of a file name, or returns null.
 *
 * Three shapes are understood, tried in this order:
 *   - two ISO days, e.g. `BusinessReport-2026-08-01-2026-08-31.csv`
 *   - two US days with two- or four-digit years, e.g. `...8-1-26_8-31-26...`
 *     or `8/1/2026 to 8/31/2026`
 *   - a single month, e.g. `2026-08` or `report_2026_08.csv`, expanded to the
 *     first and last day of that month.
 * A guess is returned only when both endpoints are real days and start <= end.
 */
export function parseFilenamePeriod(name: string): FilenamePeriod | null {
  return isoRange(name) ?? usRange(name) ?? singleMonth(name);
}

/** Two ISO days: `2026-08-01` ... `2026-08-31`, any non-digit run between them. */
function isoRange(name: string): FilenamePeriod | null {
  const match = /(?<!\d)(\d{4})-(\d{2})-(\d{2})[^\d]+(\d{4})-(\d{2})-(\d{2})(?!\d)/.exec(name);
  if (!match) return null;
  const [, y1, m1, d1, y2, m2, d2] = match;
  return rangeFrom(Number(y1), Number(m1), Number(d1), Number(y2), Number(m2), Number(d2));
}

/** Two US days: `M-D-YY`/`M/D/YYYY`, separated by a non-slash, non-dash run. */
function usRange(name: string): FilenamePeriod | null {
  // A year is exactly two or four digits — never three, and never a run of a
  // longer number caught in passing.
  const day = String.raw`(\d{1,2})[-/](\d{1,2})[-/](\d{4}|\d{2})`;
  const match = new RegExp(`(?<!\\d)${day}[^\\d/-]+${day}(?!\\d)`).exec(name);
  if (!match) return null;
  const [, m1, d1, y1, m2, d2, y2] = match;
  return rangeFrom(
    fullYear(y1 as string),
    Number(m1),
    Number(d1),
    fullYear(y2 as string),
    Number(m2),
    Number(d2),
  );
}

/** A lone `YYYY-MM` (also `_` or `.` separated), expanded to the whole month. */
function singleMonth(name: string): FilenamePeriod | null {
  const match = /(?<!\d)(\d{4})[-_.](\d{2})(?![-_.]?\d)/.exec(name);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (!isRealDay(year, month, 1)) return null;
  return { start: ymd(year, month, 1), end: ymd(year, month, lastDayOfMonth(year, month)) };
}

/**
 * A single, unambiguous day from a file name, for the snapshot and listing
 * kinds (which carry a report date, not a range). Amazon file names often hold
 * a long numeric report id, so only clearly-dated forms are accepted and a
 * two-digit year is not — it is too easily a fragment of an id:
 *   - ISO `2026-09-15`
 *   - compact `20260915` (year 2000–2099, bounded by non-digits)
 *   - US `9/15/2026` or `9-15-2026` (four-digit year)
 * Returns YYYY-MM-DD, or null when nothing unambiguous is found.
 */
export function parseFilenameDate(name: string): string | null {
  const iso = /(?<!\d)(\d{4})-(\d{2})-(\d{2})(?!\d)/.exec(name);
  if (iso) {
    const day = dayFrom(Number(iso[1]), Number(iso[2]), Number(iso[3]));
    if (day) return day;
  }

  const compact = /(?<!\d)(20\d{2})(\d{2})(\d{2})(?!\d)/.exec(name);
  if (compact) {
    const day = dayFrom(Number(compact[1]), Number(compact[2]), Number(compact[3]));
    if (day) return day;
  }

  const us = /(?<!\d)(\d{1,2})[-/](\d{1,2})[-/](\d{4})(?!\d)/.exec(name);
  if (us) {
    const day = dayFrom(Number(us[3]), Number(us[1]), Number(us[2]));
    if (day) return day;
  }

  return null;
}

/** A validated YYYY-MM-DD, or null when the triple is not a real day. */
function dayFrom(year: number, month: number, dayOfMonth: number): string | null {
  if (!isRealDay(year, month, dayOfMonth)) return null;
  return ymd(year, month, dayOfMonth);
}

function rangeFrom(
  y1: number,
  m1: number,
  d1: number,
  y2: number,
  m2: number,
  d2: number,
): FilenamePeriod | null {
  if (!isRealDay(y1, m1, d1) || !isRealDay(y2, m2, d2)) return null;
  const start = ymd(y1, m1, d1);
  const end = ymd(y2, m2, d2);
  if (start > end) return null;
  return { start, end };
}
